/**
 * Module 6D — Super-admin login page (`/admin/login`).
 *
 * Mirrors the studio login OAuth flow but mints into the SEPARATE
 * admin session collection. A successful sign-in lands on `/admin`
 * (the shell). A non-allow-listed email gets a polite "not authorized"
 * screen — same UX as a forbidden invite at studio sign-in.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/lib/admin_auth";
import { ShieldCheck } from "lucide-react";


export default function AdminLogin() {
  const { admin, loading } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && admin) {
      navigate("/admin", { replace: true });
    }
  }, [loading, admin, navigate]);

  const handleSignIn = () => {
    // Same Emergent OAuth flow as studio login; redirect lands on
    // /admin (the shell) where AdminAuthProvider's checkAuth() picks
    // up the `#session_id=…` hash and exchanges it for an admin token.
    // REMINDER: DO NOT HARDCODE the auth URL or add fallbacks.
    const redirectUrl = window.location.origin + "/admin";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
           data-testid="admin-login-loading">
        <div className="text-sm text-zinc-400">Checking admin session…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6"
         data-testid="admin-login-page">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck className="w-7 h-7 text-amber-400" />
          <div>
            <div className="text-xs tracking-[0.32em] uppercase text-amber-400/70">
              Lead Studio — Internal
            </div>
            <h1 className="text-2xl tracking-tight" style={{ fontFamily: "serif" }}>
              Super-admin sign in
            </h1>
          </div>
        </div>

        <p className="text-sm text-zinc-400 leading-relaxed mb-8">
          This is the support and operations surface. Only allow-listed
          accounts can sign in here. Every action you take is audited and
          visible to the affected studio.
        </p>

        <button
          onClick={handleSignIn}
          data-testid="admin-google-signin-btn"
          className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-medium py-3 px-4 transition-colors"
          style={{ borderRadius: 0 }}
        >
          Continue with Google
        </button>

        <p className="text-[11px] text-zinc-500 mt-6 leading-relaxed">
          By signing in you acknowledge that impersonation and
          cross-studio data access leave a permanent audit trail visible
          to the affected studio owner. Sessions expire 8 hours absolute,
          2 hours idle.
        </p>
      </div>
    </div>
  );
}
